import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FieldInput } from "@/components/FieldInput";
import { ReportPanel } from "@/components/ReportPanel";
import { api, can, errMsg, inr, todayISO } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMasters } from "@/hooks/useMasters";

const NO_MASTERS = { dse: [], fl: [], schemes: [], accounts: [] };
const FIELDS = [
  { name: "type", label: "Type", type: "select", options: ["Bank", "UPI", "Cash"], required: true },
  { name: "account_name", label: "Bank Account Name", type: "text", required: true, hint: "e.g. HDFC Current A/c, PhonePe Business QR" },
  { name: "holder_name", label: "Account Holder Name", type: "text" },
  { name: "upi_id", label: "UPI ID", type: "text", hint: "e.g. ayzal@okhdfcbank (UPI type ke liye zaroori)" },
  { name: "account_number", label: "Account No. (last digits, optional)", type: "text" },
  { name: "status", label: "Status", type: "select", options: ["Active", "Inactive"], required: true },
];

function AccountDialog({ item, onClose, onSaved }) {
  const [v, setV] = useState(item ? { ...item } : { type: "Bank", status: "Active" });
  const save = async () => {
    try {
      if (item) await api.put(`/fund-accounts/${item.account_id}`, v);
      else await api.post("/fund-accounts", v);
      toast.success("Account saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="fund-account-dialog">
        <DialogHeader><DialogTitle className="font-display">{item ? `Edit ${item.account_id}` : "Add Bank Account / UPI ID"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {FIELDS.map((f) => (
            <div key={f.name} className={f.hint ? "sm:col-span-2" : ""}>
              <FieldInput f={f} value={v[f.name]} values={v} masters={NO_MASTERS} testPrefix="fund" onChange={(x) => setV((s) => ({ ...s, [f.name]: x }))} />
            </div>
          ))}
        </div>
        <DialogFooter><Button onClick={save} data-testid="fund-account-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AccountsTable() {
  const { user } = useAuth();
  const m = useMasters();
  const [edit, setEdit] = useState(undefined);
  const admin = user.role === "admin";
  return (
    <div className="space-y-3">
      {admin && <Button size="sm" onClick={() => setEdit(null)} data-testid="add-fund-account-btn"><Plus className="h-4 w-4" /> Add Bank / UPI</Button>}
      <div className="overflow-auto border border-slate-200 rounded-xl bg-white">
        <table className="dense-table" data-testid="fund-accounts-table">
          <thead><tr><th>ID</th><th>Type</th><th>Bank Account Name</th><th>Holder</th><th>UPI ID</th><th>A/c No.</th><th>Status</th>
            <th className="text-right">Total Received</th><th className="text-right">Total Transferred</th><th className="text-right">Current Balance</th>{admin && <th />}</tr></thead>
          <tbody>
            {m.accounts.map((a) => (
              <tr key={a.account_id} data-testid={`fund-row-${a.account_id}`}>
                <td className="num font-semibold">{a.account_id}</td><td>{a.type}</td><td className="font-medium">{a.account_name}</td>
                <td>{a.holder_name}</td><td className="num">{a.upi_id}</td><td className="num">{a.account_number}</td>
                <td><span className={`text-[11px] px-1.5 py-0.5 rounded border ${a.status === "Active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500"}`}>{a.status}</span></td>
                <td className="num text-right text-emerald-700">{inr(a.received)}</td>
                <td className="num text-right text-red-600">{inr(a.transferred)}</td>
                <td className={`num text-right font-semibold ${a.balance < 0 ? "text-red-600" : ""}`} data-testid={`fund-balance-${a.account_id}`}>{inr(a.balance)}</td>
                {admin && <td><button onClick={() => setEdit(a)} data-testid={`edit-fund-${a.account_id}`}><Pencil className="h-3.5 w-3.5 text-slate-500" /></button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edit !== undefined && <AccountDialog item={edit} onClose={() => setEdit(undefined)} onSaved={m.reload} />}
    </div>
  );
}

function Reconcile() {
  const { user, bump } = useAuth();
  const m = useMasters();
  const [v, setV] = useState({ as_of: todayISO() });
  const [res, setRes] = useState(null);
  useEffect(() => { setRes(null); }, [v.fund_account_id]);
  const fields = [
    { name: "fund_account_id", label: "Bank Account / UPI ID", type: "fund", required: true },
    { name: "as_of", label: "Statement date", type: "date", required: true },
    { name: "statement_balance", label: "Bank statement closing balance (₹)", type: "money", required: true },
  ];
  const save = async () => {
    if (!v.fund_account_id || v.statement_balance === undefined) return toast.error("Account and statement balance required");
    try {
      const { data } = await api.put(`/fund-accounts/${v.fund_account_id}/statement`, v);
      setRes(data);
      bump();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-5">
      <span className="hint-tag">Bank statement ka balance daalein → system balance se difference dekhein → neeche entries tick karke match karein</span>
      {can(user, "entry:cash") && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 grid sm:grid-cols-4 gap-3 items-end">
          {fields.map((f) => <FieldInput key={f.name} f={f} value={v[f.name]} values={v} masters={m} testPrefix="recon" onChange={(x) => setV((s) => ({ ...s, [f.name]: x }))} />)}
          <Button onClick={save} data-testid="recon-save-btn">Check difference</Button>
        </div>
      )}
      {res && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 fade-up" data-testid="recon-result">
          {[["Statement balance", res.statement_balance], ["System balance", res.system_balance], ["Reconciled (ticked)", res.reconciled_balance],
            ["Difference (Stmt − System)", res.difference]].map(([l, x]) => (
            <div key={l} className="bg-white border border-slate-200 rounded-xl px-4 py-3">
              <div className="text-[11px] uppercase text-slate-500">{l}</div>
              <div className={`num text-lg font-bold ${l.startsWith("Diff") && x !== 0 ? "text-red-600" : ""}`}>{inr(x)}</div>
            </div>
          ))}
          <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
            <div className="text-[11px] uppercase text-slate-500">Unreconciled entries</div>
            <div className="num text-lg font-bold">{res.unreconciled_count}</div>
          </div>
        </div>
      )}
      {v.fund_account_id && <ReportPanel key={v.fund_account_id} reportKey="fund-accounts" filters={["fund_account_id"]} initialFilters={{ fund_account_id: v.fund_account_id }} />}
    </div>
  );
}

export default function Accounts() {
  return (
    <div className="space-y-6">
      <div>
        <div className="h-1 w-14 rounded-full mb-3 bg-[#059669]" />
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Bank / UPI Accounts</h1>
        <span className="hint-tag mt-2">Har payment kis bank account ya UPI ID mein aayi/gayi — account-wise received, transferred aur balance</span>
      </div>
      <Tabs defaultValue="accounts">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="accounts" data-testid="accounts-tab-list">Accounts</TabsTrigger>
          <TabsTrigger value="report" data-testid="accounts-tab-report">Account-wise Report</TabsTrigger>
          <TabsTrigger value="search" data-testid="accounts-tab-search">Search & Link</TabsTrigger>
          <TabsTrigger value="reconcile" data-testid="accounts-tab-reconcile">Reconcile</TabsTrigger>
        </TabsList>
        <TabsContent value="accounts" className="mt-4"><AccountsTable /></TabsContent>
        <TabsContent value="report" className="mt-4">
          <p className="text-[11px] text-slate-500 mb-2">Account select karke date-wise statement dekhein aur Excel/PDF export karein.</p>
          <ReportPanel reportKey="fund-accounts" filters={["fund_account_id"]} />
        </TabsContent>
        <TabsContent value="search" className="mt-4">
          <p className="text-[11px] text-slate-500 mb-2">UTR, UPI ID, account name ya amount se dhoondhein. "Unlinked only" se purani entries ko account se link karein (Admin).</p>
          <ReportPanel reportKey="payment-search" filters={["q", "amount", "fund_account_id", "linked"]} />
        </TabsContent>
        <TabsContent value="reconcile" className="mt-4"><Reconcile /></TabsContent>
      </Tabs>
    </div>
  );
}
